import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  CalendarBlank,
  UsersThree,
  MapPinLine,
  ChartBarHorizontal,
  ArrowsClockwise,
  Truck,
  Airplane,
  Wrench,
  UploadSimple,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import CsvUploadDialog from "@/components/CsvUploadDialog";

const NAV = [
  { to: "/schedule", label: "SCHEDULE", icon: CalendarBlank, testid: "nav-schedule" },
  { to: "/rota", label: "STAFF ROTA", icon: Airplane, testid: "nav-rota" },
  { to: "/fleet", label: "FLEET ROTA", icon: Wrench, testid: "nav-fleet" },
  { to: "/coverage", label: "COVERAGE", icon: ChartBarHorizontal, testid: "nav-coverage" },
  { to: "/staff", label: "STAFF", icon: UsersThree, testid: "nav-staff" },
  { to: "/routes", label: "ROUTES", icon: MapPinLine, testid: "nav-routes" },
];

export default function Layout() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const loadStats = async () => {
    try {
      const s = await api.stats();
      setStats(s);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const reseed = async () => {
    if (!window.confirm("This will REPLACE all current data with the original CSV seed. Continue?")) return;
    setSeeding(true);
    try {
      const r = await api.seed();
      toast.success(`Reloaded: ${r.staff} staff · ${r.routes} routes · ${r.assignments} assignments`);
      await loadStats();
      navigate(0);
    } catch (e) {
      toast.error("Reseed failed");
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F8F9FA]">
      {/* Sidebar */}
      <aside
        className="w-[240px] shrink-0 bg-[#0A0A0A] text-white flex flex-col"
        data-testid="sidebar"
      >
        <div className="px-5 pt-6 pb-4 border-b border-zinc-800">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-white text-black flex items-center justify-center">
              <Truck size={20} weight="bold" />
            </div>
            <div className="font-heading font-black text-lg tracking-tight leading-none">
              SM PLANNER
            </div>
          </div>
          <div className="text-[10px] tracking-widest text-zinc-500 font-mono-plex uppercase">
            Forecast & Schedule
          </div>
        </div>

        <nav className="flex-1 py-4">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-3 text-xs tracking-widest font-semibold transition-colors border-l-2 ${
                  isActive
                    ? "bg-zinc-900 border-white text-white"
                    : "border-transparent text-zinc-400 hover:bg-zinc-900 hover:text-white"
                }`
              }
            >
              <Icon size={16} weight="bold" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-zinc-800 space-y-3">
          {stats && (
            <div className="grid grid-cols-3 gap-2 font-mono-plex" data-testid="sidebar-stats">
              <Stat label="STAFF" value={stats.staff} />
              <Stat label="RTES" value={stats.routes} />
              <Stat label="ASSN" value={stats.assignments} />
            </div>
          )}
          <button
            onClick={() => setUploadOpen(true)}
            data-testid="upload-csv-btn"
            className="w-full flex items-center justify-center gap-2 border border-white bg-white text-black hover:bg-zinc-200 transition-colors py-2 text-[10px] tracking-widest font-semibold active:scale-[0.98]"
          >
            <UploadSimple size={12} weight="bold" />
            UPLOAD NEW CSV
          </button>
          <button
            onClick={reseed}
            disabled={seeding}
            data-testid="reseed-btn"
            className="w-full flex items-center justify-center gap-2 border border-white/30 hover:bg-white hover:text-black transition-colors py-2 text-[10px] tracking-widest font-semibold active:scale-[0.98]"
          >
            <ArrowsClockwise size={12} weight="bold" />
            {seeding ? "SEEDING…" : "RELOAD BUNDLED SEED"}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>

      <CsvUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onSuccess={() => {
          loadStats();
          navigate(0);
        }}
      />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-center">
      <div className="text-[9px] text-zinc-500 tracking-widest">{label}</div>
      <div className="text-white text-sm font-bold">{value}</div>
    </div>
  );
}
